import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { Layout } from "../components/Layout";
import {
  clientService,
  Client,
  CreateClientData,
  EntityType,
} from "../services/client.service";

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<CreateClientData>({
    companyName: "",
    emailAddress: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "United States",
    phone1: "",
    phone2: "",
    fax: "",
    typeID: 5,
    contactFirstname: "",
    contactLastname: "",
    contactEmail: "",
    contactPhone: "",
    contactTitle: "",
    contactUsername: "",
    contactPassword: "",
  });

  useEffect(() => {
    loadClients();
    loadEntityTypes();
  }, []);

  const loadEntityTypes = async () => {
    try {
      const types = await clientService.getEntityTypes();
      setEntityTypes(types);
    } catch (err) {
      console.error("Failed to load entity types:", err);
    }
  };

  const loadClients = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await clientService.getClients(search);
      setClients(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadClients();
  };

  const handleCreate = async () => {
    setCreating(true);
    setError("");

    try {
      const result = await clientService.createClient(formData);
      setModalOpen(false);
      navigate(`/clients/${result.clientId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create client");
      setCreating(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      companyName: "",
      emailAddress: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
      country: "United States",
      phone1: "",
      phone2: "",
      fax: "",
      typeID: 5,
      contactFirstname: "",
      contactLastname: "",
      contactEmail: "",
      contactPhone: "",
      contactTitle: "",
      contactUsername: "",
      contactPassword: "",
    });
    setModalOpen(true);
  };

  return (
    <Layout>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant='h4'>Clients</Typography>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Create Client
          </Button>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder='Search by company name, email, or city...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: <Button onClick={handleSearch}>Search</Button>,
            }}
          />
        </Paper>

        {loading ? (
          <Box display='flex' justifyContent='center' p={4}>
            <CircularProgress />
          </Box>
        ) : clients.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Primary Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>City, State</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>{client.companyName}</TableCell>
                    <TableCell>{client.primaryContactName || "-"}</TableCell>
                    <TableCell>{client.emailAddress}</TableCell>
                    <TableCell>{client.phone1}</TableCell>
                    <TableCell>
                      {client.city}, {client.state}
                    </TableCell>
                    <TableCell>{client.statusName || "Active"}</TableCell>
                    <TableCell>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color='text.secondary'>No clients found</Typography>
          </Paper>
        )}

        {/* Create Client Modal */}
        <Dialog
          open={modalOpen}
          onClose={() => !creating && setModalOpen(false)}
          maxWidth='md'
          fullWidth
        >
          <DialogTitle>Create New Client</DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Typography variant='h6'>Company Information</Typography>
              <TextField
                label='Company Name'
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label='Company Email'
                type='email'
                value={formData.emailAddress}
                onChange={(e) =>
                  setFormData({ ...formData, emailAddress: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                select
                label='Entity Type'
                value={formData.typeID}
                onChange={(e) =>
                  setFormData({ ...formData, typeID: parseInt(e.target.value) })
                }
                fullWidth
                required
              >
                {entityTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.description}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Address Line 1'
                value={formData.address1}
                onChange={(e) =>
                  setFormData({ ...formData, address1: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label='Address Line 2'
                value={formData.address2}
                onChange={(e) =>
                  setFormData({ ...formData, address2: e.target.value })
                }
                fullWidth
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: 2,
                }}
              >
                <TextField
                  label='City'
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                  required
                />
                <TextField
                  label='State'
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({ ...formData, state: e.target.value })
                  }
                  required
                />
                <TextField
                  label='Zip Code'
                  value={formData.zipcode}
                  onChange={(e) =>
                    setFormData({ ...formData, zipcode: e.target.value })
                  }
                  required
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                }}
              >
                <TextField
                  label='Phone'
                  value={formData.phone1}
                  onChange={(e) =>
                    setFormData({ ...formData, phone1: e.target.value })
                  }
                  required
                />
                <TextField
                  label='Phone 2'
                  value={formData.phone2}
                  onChange={(e) =>
                    setFormData({ ...formData, phone2: e.target.value })
                  }
                />
                <TextField
                  label='Fax'
                  value={formData.fax}
                  onChange={(e) =>
                    setFormData({ ...formData, fax: e.target.value })
                  }
                />
              </Box>

              <Typography variant='h6' sx={{ mt: 2 }}>
                Primary Contact
              </Typography>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <TextField
                  label='First Name'
                  value={formData.contactFirstname}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactFirstname: e.target.value,
                    })
                  }
                  required
                />
                <TextField
                  label='Last Name'
                  value={formData.contactLastname}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactLastname: e.target.value,
                    })
                  }
                  required
                />
              </Box>
              <TextField
                label='Contact Email'
                type='email'
                value={formData.contactEmail}
                onChange={(e) =>
                  setFormData({ ...formData, contactEmail: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label='Contact Phone'
                value={formData.contactPhone}
                onChange={(e) =>
                  setFormData({ ...formData, contactPhone: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label='Title'
                value={formData.contactTitle}
                onChange={(e) =>
                  setFormData({ ...formData, contactTitle: e.target.value })
                }
                fullWidth
              />
              <TextField
                label='Username'
                value={formData.contactUsername}
                onChange={(e) =>
                  setFormData({ ...formData, contactUsername: e.target.value })
                }
                fullWidth
                required
              />
              <TextField
                label='Password'
                type='password'
                value={formData.contactPassword}
                onChange={(e) =>
                  setFormData({ ...formData, contactPassword: e.target.value })
                }
                fullWidth
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant='contained'
              disabled={creating}
            >
              {creating ? "Creating..." : "Create Client"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ClientsPage;
